import { create } from "zustand";
import api, { setAccessToken, setSessionId, onUnauthorized } from "./api";

const LS_TOKEN = "cl.token";
const LS_SCOPE = "cl.scope";
const SS_SESSION = "cl.session_id";

function _uuid() {
  // Simple UUIDv4-ish generator; not crypto-secure but analytics-safe.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function decodeJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const p = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = "=".repeat((4 - (p.length % 4)) % 4);
    return JSON.parse(atob(p + pad));
  } catch {
    return null;
  }
}

export const useAuth = create((set, get) => ({
  user: null,
  token: null,
  scope: null,
  ready: false,

  hydrate: async () => {
    const t = localStorage.getItem(LS_TOKEN);
    // Restore or generate a session id so analytics can group ui events.
    let sid = sessionStorage.getItem(SS_SESSION);
    if (!sid) {
      sid = _uuid();
      sessionStorage.setItem(SS_SESSION, sid);
    }
    setSessionId(sid);
    if (!t) {
      set({ ready: true });
      return;
    }
    setAccessToken(t);
    const payload = decodeJwtPayload(t);
    const scope = payload?.scope || localStorage.getItem(LS_SCOPE) || "full";
    try {
      const { data } = await api.get("/auth/me");
      // Phase 5b: refresh the httpOnly session cookie so a page reload keeps
      // /linkforge/merch/* unlocked. Awaited intentionally; if it fails we
      // treat the session as invalid rather than racing into an authed state.
      await api.post("/auth/session-cookie");
      set({ user: data, token: t, scope, ready: true });
    } catch {
      localStorage.removeItem(LS_TOKEN);
      localStorage.removeItem(LS_SCOPE);
      setAccessToken(null);
      set({ user: null, token: null, scope: null, ready: true });
    }
  },

  login: async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    const payload = decodeJwtPayload(data.access_token);
    const scope = payload?.scope || (data.user.must_reset_password ? "reset_only" : "full");
    localStorage.setItem(LS_TOKEN, data.access_token);
    localStorage.setItem(LS_SCOPE, scope);
    // Fresh session on every login.
    const sid = _uuid();
    sessionStorage.setItem(SS_SESSION, sid);
    setSessionId(sid);
    setAccessToken(data.access_token);
    // Phase 5b: mint the httpOnly `cl_access_token` cookie BEFORE returning
    // so the caller can safely navigate to a /linkforge/merch/* ?next=
    // target without racing the cookie into existence. Full-scope only;
    // reset_only users still see /reset-password and don't need the bridge.
    if (scope === "full") {
      await api.post("/auth/session-cookie");
    }
    set({ user: data.user, token: data.access_token, scope });
    return { user: data.user, scope };
  },

  resetPassword: async (newPassword) => {
    const { data } = await api.post("/auth/reset-password", { new_password: newPassword });
    localStorage.setItem(LS_TOKEN, data.access_token);
    localStorage.setItem(LS_SCOPE, "full");
    setAccessToken(data.access_token);
    // Phase 5b: the fresh full-scope token also deserves a bridge cookie.
    await api.post("/auth/session-cookie");
    set({ user: data.user, token: data.access_token, scope: "full" });
    return data.user;
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    try {
      // Phase 5b: wipe the httpOnly bridge cookie too. Ignore failures;
      // local state is cleared below either way.
      await api.post("/auth/session-cookie-clear");
    } catch {
      /* ignore */
    }
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_SCOPE);
    setAccessToken(null);
    set({ user: null, token: null, scope: null });
  },

  clear: () => {
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_SCOPE);
    setAccessToken(null);
    set({ user: null, token: null, scope: null });
  },
}));

// Auto-clear session on 401 anywhere in the app.
onUnauthorized(() => {
  useAuth.getState().clear();
});
